/**
 * AI Chat Mention System - Page Navigation Helper
 *
 * Auto-configures button navigation from page mentions.
 * When a user creates a button with a #PageName mention, this helper
 * automatically sets up the navigation properties.
 */

import type { Component, ComponentInput, ComponentType } from '@/types';
import type { ResolvedMentionData } from './types';

// ============================================================================
// Functions
// ============================================================================

/**
 * Apply page mention navigation to a button component
 *
 * Automatically configures a button's navigation properties based on
 * resolved page mentions. Only applies to button components that don't
 * already have an action configured (or have 'navigate' action).
 *
 * @param component - The component to potentially update
 * @param pageMentions - Array of resolved mentions (may include data mentions)
 * @returns Updated component if navigation was applied, original component otherwise
 *
 * @example
 * // User says: "Create a button to go to #Dashboard"
 * const updated = applyPageMentionToButton(buttonComponent, resolvedMentions);
 * // updated.properties.action = 'navigate'
 * // updated.properties.target = 'dashboard-page-id'
 * // updated.properties.navigationLabel = 'Dashboard'
 */
export function applyPageMentionToButton(
  component: ComponentInput,
  pageMentions: (ResolvedMentionData | null)[]
): ComponentInput {
  // Only apply to button components
  // Check both string type and enum type for compatibility
  if (component.type !== 'button' && component.type !== ('button' as ComponentType)) {
    return component;
  }

  // Find the first resolved page mention
  const firstPageMention = pageMentions.find(
    (m): m is ResolvedMentionData =>
      m !== null && m.type === 'page' && !!m.page
  );

  if (!firstPageMention || !firstPageMention.page) {
    return component;
  }

  // Don't override if action is already set to something other than 'navigate'
  const currentAction = component.properties?.action;
  if (currentAction && currentAction !== 'navigate') {
    return component;
  }

  // Apply navigation properties
  return {
    ...component,
    properties: {
      ...component.properties,
      action: 'navigate',
      target: firstPageMention.page.id,
      navigationLabel: firstPageMention.page.name,
    },
  };
}

/**
 * Apply page mentions to multiple button components
 *
 * Batch helper for applying navigation to multiple components.
 *
 * @param components - Array of components to potentially update
 * @param pageMentions - Array of resolved mentions
 * @returns Updated components array with navigation applied to buttons
 */
export function applyPageMentionsToComponents(
  components: Component[],
  pageMentions: (ResolvedMentionData | null)[]
): Component[] {
  return components.map((component) =>
    applyPageMentionToButton(component, pageMentions) as Component
  );
}
